import { Formik } from "formik";
import { Lock, ShieldCheck } from "lucide-react";
import * as yup from "yup";

import { FormikInput } from "@src/shared/components/forms";
import { ErrorMessage } from "@src/shared/components/general/ErrorMessage";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Skeleton } from "@src/shared/components/ui/skeleton";
import { Spinner } from "@src/shared/components/ui/spinner";

export type SecuritySettingsFormValues = {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export interface SecuritySettingsPageV2Props {
  canChangePassword: boolean;
  isLoadingAuthMethods?: boolean;
  isSaving?: boolean;
  providerLabels?: string[];
  saveError?: string | null;
  onSubmit: (values: SecuritySettingsFormValues) => void | Promise<void>;
}

const emptyValues: SecuritySettingsFormValues = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const validationSchema = yup.object<SecuritySettingsFormValues>({
  currentPassword: yup.string().required("Current password is required."),
  newPassword: yup
    .string()
    .min(8, "Password must be at least 8 characters.")
    .required("New password is required."),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], "Passwords must match.")
    .required("Confirm your new password."),
});

export function SecuritySettingsPageV2({
  canChangePassword,
  isLoadingAuthMethods = false,
  isSaving = false,
  providerLabels = [],
  saveError = null,
  onSubmit,
}: SecuritySettingsPageV2Props) {
  return (
    <VerticalFlexbox
      className="mx-auto w-full max-w-[720px] px-4 py-6 md-stitch:px-8 md-stitch:py-8"
      gap="none"
    >
      <HeaderText tag="h2" className="mb-2 text-[22px] tracking-tight">
        Security
      </HeaderText>
      <Text appearance="subdued" className="mb-8" sizing="sm">
        Change your password and manage sign-in for this account.
      </Text>

      {isLoadingAuthMethods ? (
        <VerticalFlexbox aria-label="Loading security settings" gap="md">
          <Skeleton className="h-16 rounded-[12px]" />
          <Skeleton className="h-48 rounded-[12px]" />
        </VerticalFlexbox>
      ) : canChangePassword ? (
        <Formik<SecuritySettingsFormValues>
          initialValues={emptyValues}
          validationSchema={validationSchema}
          onSubmit={async (values, helpers) => {
            await onSubmit(values);
            helpers.resetForm();
          }}
        >
          {({ dirty, handleSubmit, isSubmitting }) => {
            const submitting = isSaving || isSubmitting;

            return (
              <VerticalFlexbox as="form" gap="none" onSubmit={handleSubmit}>
                <VerticalFlexbox
                  aria-labelledby="password-settings-heading"
                  as="section"
                  gap="md"
                >
                  <Text
                    as="h3"
                    fontWeight={600}
                    id="password-settings-heading"
                    sizing="sm"
                  >
                    Password
                  </Text>

                  <FormikInput
                    autoComplete="current-password"
                    disabled={submitting}
                    label="Current password"
                    name="currentPassword"
                    required
                    type="password"
                  />
                  <FormikInput
                    autoComplete="new-password"
                    disabled={submitting}
                    hint="At least 8 characters"
                    label="New password"
                    name="newPassword"
                    required
                    type="password"
                  />
                  <FormikInput
                    autoComplete="new-password"
                    disabled={submitting}
                    label="Confirm new password"
                    name="confirmPassword"
                    required
                    type="password"
                  />

                  <ErrorMessage error={saveError} />

                  <Button
                    className="w-full sm-stitch:w-auto"
                    disabled={!dirty || submitting}
                    type="submit"
                  >
                    {submitting ? <Spinner className="mr-1" size="sm" /> : null}
                    {submitting ? "Updating..." : "Update password"}
                  </Button>
                </VerticalFlexbox>
              </VerticalFlexbox>
            );
          }}
        </Formik>
      ) : (
        <HorizontalFlexbox
          alignItems="flex-start"
          aria-labelledby="external-sign-in-heading"
          as="section"
          className="rounded-[12px] border border-border bg-background px-5 py-4"
          gap="md"
        >
          <Lock
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-muted-foreground"
          />
          <VerticalFlexbox gap="xs">
            <Text
              as="h3"
              fontWeight={600}
              id="external-sign-in-heading"
              sizing="sm"
            >
              Password managed by your sign-in provider
            </Text>
            <Text appearance="subdued" sizing="sm">
              Password changes are only available for accounts created with an
              email and password.
            </Text>
            <HorizontalFlexbox alignItems="center" gap="xs">
              <ShieldCheck aria-hidden="true" className="size-4 text-primary" />
              <Text appearance="subdued" sizing="sm">
                {providerLabels.length > 0
                  ? `Signed in with ${providerLabels.join(", ")}`
                  : "Signed in with an external provider"}
              </Text>
            </HorizontalFlexbox>
          </VerticalFlexbox>
        </HorizontalFlexbox>
      )}
    </VerticalFlexbox>
  );
}
